/**
 * Plan intent from a sign-up link (`/sign-up?plan=protect&interval=yearly`).
 *
 * The pricing page puts the chosen plan in the link. This module is
 * the ONLY place the dashboard reads it, and it treats it as a hint:
 *
 *   - `plan` must match a short lowercase slug grammar and `interval`
 *     must be exactly `monthly` or `yearly`; anything else is dropped
 *     silently and never blocks sign-in.
 *   - The value is stored in localStorage (the magic link opens in a
 *     new tab, so sessionStorage would lose it) with a 7-day expiry.
 *   - It grants nothing. A plan only changes through a server-side
 *     checkout that re-checks the plan is active, public and for sale.
 *     The value is never put into the DOM as HTML, never used to build
 *     a URL path, and never sent anywhere by this module.
 */

export type PlanIntervalIntent = 'monthly' | 'yearly';

export interface PlanIntent {
  readonly plan: string;
  readonly interval: PlanIntervalIntent;
  readonly saved_at: string;
}

export const PLAN_INTENT_STORAGE_KEY = 'sill_plan_intent';
export const PLAN_INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** Validate the raw `plan` / `interval` query values. */
export function sanitizePlanIntentParams(
  rawPlan: string | null,
  rawInterval: string | null,
): { plan: string; interval: PlanIntervalIntent } | null {
  if (rawPlan === null) return null;
  const plan = rawPlan.trim().toLowerCase();
  if (!SLUG_RE.test(plan)) return null;
  const interval: PlanIntervalIntent = rawInterval === 'yearly' ? 'yearly' : 'monthly';
  return { plan, interval };
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Read `plan` / `interval` from a URL and remember them. Returns the
 *  stored intent, or null when the URL carries none (an existing
 *  intent is kept). */
export function capturePlanIntentFromUrl(
  url: URL,
  now: Date = new Date(),
): PlanIntent | null {
  const clean = sanitizePlanIntentParams(
    url.searchParams.get('plan'),
    url.searchParams.get('interval'),
  );
  if (clean === null) return null;
  const intent: PlanIntent = { ...clean, saved_at: now.toISOString() };
  try {
    storage()?.setItem(PLAN_INTENT_STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // Private mode or storage full: the link still signs in.
  }
  return intent;
}

/** Read the stored intent, dropping anything malformed or expired. */
export function readPlanIntent(now: Date = new Date()): PlanIntent | null {
  const s = storage();
  if (s === null) return null;
  let raw: string | null = null;
  try {
    raw = s.getItem(PLAN_INTENT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    const clean = sanitizePlanIntentParams(
      typeof v['plan'] === 'string' ? v['plan'] : null,
      typeof v['interval'] === 'string' ? v['interval'] : null,
    );
    const savedAt = typeof v['saved_at'] === 'string' ? Date.parse(v['saved_at']) : Number.NaN;
    if (
      clean === null
      || Number.isNaN(savedAt)
      || now.getTime() - savedAt > PLAN_INTENT_TTL_MS
      || savedAt - now.getTime() > 60_000
    ) {
      clearPlanIntent();
      return null;
    }
    return { ...clean, saved_at: new Date(savedAt).toISOString() };
  } catch {
    clearPlanIntent();
    return null;
  }
}

export function clearPlanIntent(): void {
  try {
    storage()?.removeItem(PLAN_INTENT_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
